import React, { CSSProperties, ReactElement } from 'react';
import clsx from 'clsx';
import { Progress, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { ColumnProps, SortOrder } from 'antd/lib/table';
import { OperationType } from '../../types/SelectedOperation';
import { Sorting } from 'src/types/Sorting';
import TextWithEllipsis from 'src/components/common/TextWithEllipsis';
import { QaError } from '../../types/QaErrors';
import ScriptActivationIcon from '../../ScriptActivationIcon';
import styles from './QaErrorsTable.module.less';

export const defaultPagination = {
  hideOnSinglePage: false,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
  locale: { items_per_page: '' },
};

const MAX_ROW_LENGTH = 30;

export type ColumnId = keyof QaError;
export type ColumnSorting = Sorting<QaError> & { field: ColumnId };
export type StandardColumn = ColumnProps<QaError>;
export type FilterableColumn = StandardColumn & { dataIndex: ColumnId };
export type QaErrorsColumn = FilterableColumn | StandardColumn;

export type ClickHandler = (row: QaError) => void;

const renderDetailsLink = (
  value: string | null
  // record: QaError
) => {
  if (value) {
    // const { siteCode, scriptName, applicationId } = record;
    return (
      // TODO: this should be commented until https://ql2.atlassian.net/browse/UI-3157 is done
      // <Link to={`${routes.errorMonitoring}/${applicationId}/${siteCode}/${scriptName}`} target="_blank">
      <TextWithEllipsis value={value} maxLength={MAX_ROW_LENGTH} />
      // </Link>
    );
  }
};

const renderErrorCodeTitle = (excludeErrorCode: string[], title?: string | ReactElement) => {
  const header = title ? <div>{title}.</div> : '';
  return (
    <div className={styles.tooltipList}>
      {header}
      Excluded Error Codes:
      <div className={styles.errorCodeList}>
        <ul>
          {[...excludeErrorCode].sort().map((errorCode) => (
            <li key={errorCode}>{errorCode}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const renderCurrentError = (record: QaError) => {
  const currentErrorInfo: { title: ReactElement | string; className?: string } = {
    title: 'Current Error % is Below Threshold',
  };
  if (record.errorThreshold < record.currentError) {
    currentErrorInfo.title = 'Current Error % is Above Threshold';
    currentErrorInfo.className = styles.above;
  } else if (record.errorThreshold === record.currentError) {
    currentErrorInfo.title = 'Current Error % is Equal to Threshold';
  }
  if (record.excludeErrorCode) {
    currentErrorInfo.title = renderErrorCodeTitle(record.excludeErrorCode, currentErrorInfo.title);
  }

  return (
    <Tooltip
      className={clsx(styles.currentError, currentErrorInfo.className)}
      title={currentErrorInfo.title}
      overlayClassName={styles.productUrlTooltip}
    >
      {record.currentError}%
    </Tooltip>
  );
};

const renderActions = (record: QaError, onAction: (record: QaError, type: OperationType) => void) => {
  // TODO: Remove Operation types and use a function for config Script
  const handleMenuClick = () => {
    onAction(record, OperationType.ConfigScript);
  };
  return (
    <FontAwesomeIcon
      icon={faEdit}
      className={styles.actionIcon}
      title={'Configuration'}
      onClick={() => handleMenuClick()}
    />
  );
};

const renderPriority = (record: QaError, value: string) => {
  const { priorityOverridden } = record;
  let title: ReactElement | string = priorityOverridden ? 'Overridden' : 'Default';
  return priorityOverridden ? (
    <Tooltip title={title}>
      <i>{value}</i>
    </Tooltip>
  ) : (
    <Tooltip title={title}>{value}</Tooltip>
  );
};

const renderErrorThreshold = (record: QaError, value: string) => {
  const { thresholdOverridden } = record;
  let title: ReactElement | string = thresholdOverridden ? 'Overridden' : 'Default';
  if (record.excludeErrorCode) {
    title = renderErrorCodeTitle(record.excludeErrorCode, title);
  }
  return thresholdOverridden ? (
    <Tooltip title={title}>
      <i>{value}</i>
    </Tooltip>
  ) : (
    <Tooltip title={title}>{value}</Tooltip>
  );
};

const renderScriptHealth = (record: QaError) => {
  const progress = <Progress
    className={styles.progressBar}
    style={{ '--percent': record.scriptHealth } as CSSProperties}
    percent={Number(record.scriptHealth)}
    status="active"
  />
  if (record.excludeErrorCode) {
    const title = renderErrorCodeTitle(record.excludeErrorCode);
    return (
      <Tooltip title={title}>
        {progress}
      </Tooltip>
    );
  }
  return progress;
};

const getFilterableColumns = (
  onShowBugsModal: ClickHandler,
  onStatusIconClick: ClickHandler | null
): FilterableColumn[] => {
  const columns: FilterableColumn[] = [
    {
      title: 'Priority',
      dataIndex: 'priority',
      sorter: true,
      align: 'center',
      render: (_, record) => renderPriority(record, record.priority),
    },
    {
      title: 'Application',
      dataIndex: 'applicationName',
      sorter: true,
    },
    {
      title: 'Site Code',
      dataIndex: 'siteCode',
      sorter: true,
      render: renderDetailsLink,
    },
    {
      title: 'Script Name',
      dataIndex: 'scriptName',
      sorter: true,
      render: renderDetailsLink,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      sorter: true,
      align: 'center',
      render: (_, record) => {
        const statusIcon = <ScriptActivationIcon value={record.status.toLowerCase() === 'active'} />;
        return onStatusIconClick ? <a onClick={() => onStatusIconClick(record)}>{statusIcon}</a> : statusIcon;
      },
    },
    {
      title: 'Bugs',
      dataIndex: 'bugs',
      sorter: true,
      align: 'center',
      render: (_, record) => {
        return <a onClick={() => onShowBugsModal(record)}>{record.bugs}</a>;
      },
    },
    {
      title: 'Script Health',
      dataIndex: 'scriptHealth',
      sorter: true,
      align: 'center',
      render: (_, record) => renderScriptHealth(record),
    },
    {
      title: 'Error Threshold (%)',
      dataIndex: 'errorThreshold',
      sorter: true,
      align: 'center',
      render: (_, record) => renderErrorThreshold(record, `${record.errorThreshold}%`),
    },
    {
      title: 'Current Error (%)',
      dataIndex: 'currentError',
      sorter: true,
      align: 'center',
      render: (_, record) => renderCurrentError(record),
    },
  ];

  return columns;
};

export const getColumns = (
  sorting: ColumnSorting,
  onShowBugsModal: ClickHandler,
  onStatusIconClick: ClickHandler | null,
  onAction: (record: QaError, type: OperationType) => void
): QaErrorsColumn[] => {
  const sortOrder = (key: ColumnId): SortOrder | false => {
    return sorting.field === key ? sorting.order : false;
  };

  const columns = getFilterableColumns(onShowBugsModal, onStatusIconClick);
  const filteredColumns = columns.map((column) => ({
    ...column,
    ...(column.sorter && { sortOrder: sortOrder(column.dataIndex) }),
  }));

  return [
    ...filteredColumns,
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_, record: QaError) => renderActions(record, onAction),
    },
  ];
};
